import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { environmentContextState, shopifyState, toViewPriceFnState, abTestState } from '../../recoil/atoms';
import { KVP, sizeImage } from '../../util';
import { Direction, MannequinPhoto, useProductAssets } from '../../hooks/useProductAssets';
import { ViewProduct } from '../../models/ViewProduct.model';
import { DiscountAmount, DiscountTypes } from '../../discounts';
import { Shopify } from '../../models/Shopify.model';
import { SubContext } from '../../enums/SubContext.enum';
import { useProductSelection } from '../../hooks/useProductSelection';
import { SelectionType } from '../../enums/SelectionType.enum';
import { QuickViewLink } from '../../product_detail/components/QuickViewLink';
import { Color } from '../../enums/Color.enum';
import { SizeDropdown } from '../../components/SizeDropdown';
import { useProductAvailability } from '../../hooks/useVariantAvailability';
import { VariantStatus } from '../../enums/VariantStatus.enum';
import { LongSize } from '../../product_detail/components/size.enum';
import ProductFormButton from '../../product_detail/components/ProductFormButton';
import { FitDropdown } from '../../components/FitDropdown';
import { Fit } from '../../enums/Fit.enum';
import classNames from 'classnames';
import styles from './CartPromoColorItem.module.scss';
import { cartState } from '../../recoil/selectors';

interface Props {
  product: ViewProduct;
  initialSize: LongSize;
  initialFit: Fit;
  discount: DiscountAmount;
  selectionKey: string;
}

export const CartPromoColorItem: React.FC<Props> = ({ product, discount, selectionKey, initialSize, initialFit }) => {
  const selectionType = SelectionType.CartPromo;
  const shopify = useRecoilValue(shopifyState);
  const abTests = useRecoilValue(abTestState);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const productAvailability = useProductAvailability(product);
  const { size: selectedSize, fit: selectedFit, setSize, setFit } = useProductSelection(selectionType, selectionKey, product.handle);
  const { timedSaleActive } = useRecoilValue(environmentContextState);
  const size = selectedSize ?? initialSize;
  const fit = selectedFit ?? initialFit;

  // TODO handle FIT
  const availableVariants = useMemo(
    () =>
      productAvailability
        .filter((v) => v.size === size)
        .filter((v) => !v.fit || v.fit === fit)
        .filter((v) => v.status === VariantStatus.Available)
        .map((v) => product.variants.find((pv) => pv.id === v.id))
        .filter((v) => !!v),
    [size, fit, productAvailability, product.variants],
  );

  const photos = useProductAssets(product);

  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ direction }) => direction === Direction.Front);
  const mannequinPhotoMap: KVP<MannequinPhoto> = filteredMannequinPhotos.reduce((acc, photo) => ({ ...acc, [photo.color as Color]: photo }), {});
  const sizes = product.sizes;
  const fits = product.fits;

  const price = timedSaleActive ? product.timedSalePrice : product.price;

  const cart = useRecoilValue(cartState);
  const maxCartPrice =
    cart.lineItems
      .filter((li) => !li.properties.Bundle)
      .reduce((acc, { final_price, quantity }) => {
        const ppi = final_price / quantity;
        return ppi > acc ? ppi : acc;
      }, 0) / 100;

  const crossoutPrice = product.displayPrice;
  const purchasePrice = toViewPriceFn(
    maxCartPrice === 0 || price <= maxCartPrice
      ? discountedPrice(price, discount, shopify)
      : price - (maxCartPrice - discountedPrice(maxCartPrice, discount, shopify)),
  );

  const colorVariants = availableVariants.map(
    (variant) =>
      variant &&
      mannequinPhotoMap[variant.color] && (
        <div className={classNames(styles.hlCartThumbnail, { [styles.scrollable]: abTests['AB2022-09'] })} key={variant.id}>
          <img alt="product" className={styles.hlCartItemImage} src={sizeImage(mannequinPhotoMap[variant.color].src, '140x160')} />
          <ProductFormButton
            selectionKey={selectionKey}
            selectionType={selectionType}
            variantOverride={variant}
            text={'+Add'}
            buttonType={'secondary'}
            size={'tiny'}
          />
        </div>
      ),
  );

  return (
    <div className={styles.hlCartPromoColorItem}>
      <div className={styles.hlCartProduct}>
        <div>
          <QuickViewLink
            product={product}
            selectionKey={selectionKey}
            initialColor={availableVariants[0]?.color}
            subContext={SubContext.PromoCartUpsell}
            dataTrackLabel="Quick view"
            dataTrackLocation="Cart promo"
            text={product.title}
            buttonType="link"
          />
          <SizeDropdown
            type="internal"
            label="Size"
            style={{ border: '0', color: '#6261e8', paddingLeft: '0', marginLeft: '-3px', width: 'fit-content', background: 'white' }}
            onChange={(s) => setSize(s)}
            availableValues={sizes}
            initialValue={size}
            allValues={product.sizes}
          />
          {fits.length > 1 && (
            <FitDropdown
              type="internal"
              label="Fit"
              style={{ border: '0', color: '#6261e8', paddingLeft: '0', marginLeft: '10px', width: 'fit-content' }}
              onChange={(f) => setFit(f)}
              allValues={fits}
              availableValues={fits}
              initialValue={initialFit}
            />
          )}
        </div>
        <div className={styles.hlPromoColorItemPrice}>
          <span className={styles.crossout}>{crossoutPrice}</span>
          <span>{purchasePrice}</span>
        </div>
      </div>

      {abTests['AB2022-09'] && (
        <div className={classNames(styles.productColorImages, styles.scrollable)}>
          <div className={styles.productColorImagesBox}>{colorVariants}</div>
        </div>
      )}
      {!abTests['AB2022-09'] && <div className={styles.productColorImages}>{colorVariants}</div>}
    </div>
  );
};

const discountedPrice = (priceInCents: number, discount: DiscountAmount, shopify: Shopify | undefined) => {
  switch (discount.type) {
    case DiscountTypes.Cents:
      return priceInCents - centsToCurrency(discount.value, shopify);
    case DiscountTypes.Percentage:
      return priceInCents * (1 - discount.value / 100);
    default:
      return priceInCents;
  }
};
const centsToCurrency = (amountInCents: number, shopify?: Shopify) => Math.floor(amountInCents * (shopify?.currency.rate ?? 1));

export default CartPromoColorItem;
